import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Users, Package, ShoppingBag, DollarSign, LogOut, Check, X, TrendingUp, Store, Banknote } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import NotificationBell from "../components/NotificationBell";
import Navbar from "../components/Navbar";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [payoutRequests, setPayoutRequests] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, analyticsRes, vendorsRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/vendors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/payouts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDashboard(dashRes.data);
      setAnalytics(analyticsRes.data);
      setVendors(vendorsRes.data.vendors);
      setPayoutRequests(payoutsRes.data.requests);
    } catch (error) {
      console.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleVendorApproval = async (vendorId, approved, commissionRate = 0.20) => {
    try {
      await axios.put(
        `${API}/admin/vendors/${vendorId}/approve`,
        { approved, commission_rate: commissionRate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Vendor ${approved ? "approved" : "rejected"}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update vendor");
    }
  };

  const handleCommissionUpdate = async (vendorId, rate) => {
    try {
      await axios.put(
        `${API}/admin/vendors/${vendorId}/commission`,
        { commission_rate: parseFloat(rate) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Commission rate updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update commission");
    }
  };

  const handleProcessPayout = async (payoutId, approved) => {
    try {
      await axios.put(
        `${API}/admin/payouts/${payoutId}/process?approved=${approved}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Payout ${approved ? "approved" : "rejected"}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to process payout");
    }
  };

  const COLORS = ["#20C997", "#F06570", "#D4A373", "#6B7A8F", "#3E6656", "#9E4747", "#5C5D5C"];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      {/* Navbar */}
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-['Cormorant_Garamond'] text-4xl font-light text-[#1A1B1A]">
            Admin Dashboard
          </h1>
          <p className="text-[#5C5D5C] mt-1">Manage vendors, view analytics, and monitor sales</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-[#EAE6DF]">
          {["overview", "vendors", "payouts", "analytics"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 px-2 font-medium transition-colors ${
                activeTab === tab 
                  ? "text-[#20C997] border-b-2 border-[#20C997]" 
                  : "text-[#5C5D5C] hover:text-[#1A1B1A]"
              }`}
              data-testid={`admin-tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="dashboard-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#5C5D5C] text-sm">Total Users</p>
                    <p className="text-3xl font-light text-[#1A1B1A] mt-1" data-testid="total-users">
                      {dashboard?.total_users || 0}
                    </p>
                  </div>
                  <Users size={24} className="text-[#20C997]" />
                </div>
              </div>

              <div className="dashboard-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#5C5D5C] text-sm">Total Vendors</p>
                    <p className="text-3xl font-light text-[#1A1B1A] mt-1" data-testid="total-vendors">
                      {dashboard?.total_vendors || 0}
                    </p>
                  </div>
                  <Store size={24} className="text-[#F06570]" />
                </div>
              </div>

              <div className="dashboard-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#5C5D5C] text-sm">Total Products</p>
                    <p className="text-3xl font-light text-[#1A1B1A] mt-1" data-testid="admin-total-products">
                      {dashboard?.total_products || 0}
                    </p>
                  </div>
                  <Package size={24} className="text-[#3E6656]" />
                </div>
              </div>

              <div className="dashboard-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#5C5D5C] text-sm">Total Orders</p>
                    <p className="text-3xl font-light text-[#1A1B1A] mt-1" data-testid="admin-total-orders">
                      {dashboard?.total_orders || 0}
                    </p>
                  </div>
                  <ShoppingBag size={24} className="text-[#D4A373]" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="dashboard-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#5C5D5C] text-sm">Total Revenue</p>
                    <p className="text-3xl font-light text-[#1A1B1A] mt-1" data-testid="admin-total-revenue">
                      £{dashboard?.total_revenue?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <TrendingUp size={24} className="text-[#20C997]" />
                </div>
              </div>

              <div className="dashboard-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#5C5D5C] text-sm">Platform Commission</p>
                    <p className="text-3xl font-light text-[#1A1B1A] mt-1" data-testid="total-commission">
                      £{dashboard?.total_commission?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <DollarSign size={24} className="text-[#F06570]" />
                </div>
              </div>
            </div>

            {/* Pending Vendors */}
            {dashboard?.pending_vendors?.length > 0 && (
              <div>
                <h2 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1B1A] mb-4">
                  Pending Vendor Approvals
                </h2>
                <div className="space-y-4" data-testid="pending-vendors-list">
                  {dashboard.pending_vendors.map((vendor, index) => (
                    <div key={vendor.id} className="dashboard-card flex items-center justify-between" data-testid={`pending-vendor-${index}`}>
                      <div>
                        <p className="font-medium text-[#1A1B1A]">{vendor.name}</p>
                        <p className="text-sm text-[#5C5D5C]">{vendor.email}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleVendorApproval(vendor.id, true)}
                          size="sm"
                          className="bg-[#3E6656] hover:bg-[#20C997] text-white rounded-none"
                          data-testid={`approve-vendor-${index}`}
                        >
                          <Check size={16} className="mr-1" /> Approve
                        </Button>
                        <Button
                          onClick={() => handleVendorApproval(vendor.id, false)}
                          size="sm"
                          variant="outline"
                          className="border-[#9E4747] text-[#9E4747] rounded-none"
                          data-testid={`reject-vendor-${index}`}
                        >
                          <X size={16} className="mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Vendors Tab */}
        {activeTab === "vendors" && (
          <div className="space-y-4" data-testid="all-vendors-list">
            {vendors.length === 0 ? (
              <div className="dashboard-card text-center py-12">
                <Store size={48} className="mx-auto text-[#8E8F8E] mb-4" />
                <p className="text-[#5C5D5C]">No vendors registered yet</p>
              </div>
            ) : (
              vendors.map((vendor, index) => (
                <div key={vendor.id} className="dashboard-card" data-testid={`vendor-row-${index}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p className="font-medium text-[#1A1B1A]">{vendor.name}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          vendor.vendor_status === "approved" 
                            ? "bg-[#3E6656]/10 text-[#3E6656]" 
                            : vendor.vendor_status === "rejected"
                            ? "bg-[#9E4747]/10 text-[#9E4747]"
                            : "bg-[#D4A373]/10 text-[#D4A373]"
                        }`}>
                          {vendor.vendor_status}
                        </span>
                      </div>
                      <p className="text-sm text-[#5C5D5C] mt-1">{vendor.email}</p>
                      <div className="flex gap-6 mt-2 text-sm">
                        <span className="text-[#5C5D5C]">
                          Sales: <span className="text-[#1A1B1A] font-medium">£{vendor.total_sales?.toFixed(2) || "0.00"}</span>
                        </span>
                        <span className="text-[#5C5D5C]">
                          Balance: <span className="text-[#1A1B1A] font-medium">£{vendor.balance?.toFixed(2) || "0.00"}</span>
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-[#5C5D5C]">Commission:</label>
                        <select
                          value={vendor.commission_rate || 0.20}
                          onChange={(e) => handleCommissionUpdate(vendor.id, e.target.value)}
                          className="border border-[#EAE6DF] px-2 py-1 text-sm rounded-none"
                          data-testid={`commission-select-${index}`}
                        >
                          <option value="0.15">15%</option>
                          <option value="0.18">18%</option>
                          <option value="0.20">20%</option>
                        </select>
                      </div>
                      {vendor.vendor_status === "pending" && (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleVendorApproval(vendor.id, true)}
                            size="sm"
                            className="bg-[#3E6656] hover:bg-[#20C997] text-white rounded-none"
                          >
                            <Check size={16} />
                          </Button>
                          <Button
                            onClick={() => handleVendorApproval(vendor.id, false)}
                            size="sm"
                            variant="outline"
                            className="border-[#9E4747] text-[#9E4747] rounded-none"
                          >
                            <X size={16} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}


        {/* Payouts Tab */}
        {activeTab === "payouts" && (
          <div className="space-y-4" data-testid="admin-payouts">
            <h2 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1B1A] mb-4">
              Vendor Payout Requests
            </h2>
            {payoutRequests.length === 0 ? (
              <div className="dashboard-card text-center py-12">
                <Banknote size={48} className="mx-auto text-[#8E8F8E] mb-4" />
                <p className="text-[#5C5D5C]">No payout requests</p>
              </div>
            ) : (
              payoutRequests.map((payout, index) => (
                <div key={payout.id} className="dashboard-card" data-testid={`admin-payout-${index}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-[#1A1B1A]">{payout.vendor_name}</p>
                      <p className="text-sm text-[#5C5D5C]">{payout.vendor_email}</p>
                      <p className="text-xs text-[#8E8F8E] mt-1">
                        Requested {new Date(payout.requested_at).toLocaleDateString("en-GB")}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-xl font-medium text-[#1A1B1A]">£{payout.amount?.toFixed(2)}</p>
                      {payout.status === "pending" ? (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleProcessPayout(payout.id, true)}
                            size="sm"
                            className="bg-[#3E6656] hover:bg-[#20C997] text-white rounded-none"
                            data-testid={`approve-payout-${index}`}
                          >
                            <Check size={16} className="mr-1" /> Approve
                          </Button>
                          <Button
                            onClick={() => handleProcessPayout(payout.id, false)}
                            size="sm"
                            variant="outline"
                            className="border-[#9E4747] text-[#9E4747] rounded-none"
                            data-testid={`reject-payout-${index}`}
                          >
                            <X size={16} className="mr-1" /> Reject
                          </Button>
                        </div>
                      ) : (
                        <span className={`text-xs px-3 py-1 rounded-full ${
                          payout.status === "approved" ? "bg-[#3E6656]/10 text-[#3E6656]" : "bg-[#9E4747]/10 text-[#9E4747]"
                        }`}>
                          {payout.status}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <div className="space-y-8">
            {/* Monthly Revenue Chart */}
            <div className="dashboard-card">
              <h3 className="font-medium text-[#1A1B1A] mb-6">Monthly Revenue</h3>
              <div className="h-80" data-testid="revenue-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics?.monthly_revenue || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DF" />
                    <XAxis dataKey="month" stroke="#5C5D5C" />
                    <YAxis stroke="#5C5D5C" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "#fff", 
                        border: "1px solid #EAE6DF",
                        borderRadius: 0 
                      }} 
                    />
                    <Bar dataKey="revenue" fill="#20C997" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Sales by Category */}
            <div className="dashboard-card">
              <h3 className="font-medium text-[#1A1B1A] mb-6">Sales by Category</h3>
              <div className="h-80" data-testid="category-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analytics?.sales_by_category || []}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ _id, percent }) => `${_id} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      dataKey="total_sales"
                    >
                      {(analytics?.sales_by_category || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "#fff", 
                        border: "1px solid #EAE6DF",
                        borderRadius: 0 
                      }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
